import { cn } from '@/lib/utils';

// name/color optional so callers can pass the loose `Job.tags` list shape.
type JobTag = { tagId: string; name?: string; color?: string | null };

/**
 * Colored job tags rendered as compact chips in front of the customer name on a
 * dispatch-board / calendar tile. Mirrors the tag pill used in the contacts
 * grid (ContactTagsCell) — a tinted pill with a solid colour dot — but sized
 * down for a dense tile. Caps the visible count and folds the rest into a "+N"
 * so a heavily-tagged job never blows out the lane width.
 */
export function JobTagChips({
  tags,
  max = 2,
  compact = false,
  className,
}: {
  tags?: JobTag[] | null;
  max?: number;
  /** Color dots only for one-line tiles where labels would displace time/status. */
  compact?: boolean;
  className?: string;
}) {
  if (!tags || tags.length === 0) return null;
  const shown = tags.slice(0, max);
  const overflow = tags.length - shown.length;
  if (compact) {
    const names = tags.map((tag) => tag.name).filter(Boolean).join(', ');
    return (
      <span
        className={cn(
          'pointer-events-none absolute inset-x-1 bottom-0.5 flex h-0.5 overflow-hidden rounded-full',
          className,
        )}
        title={names}
        aria-label={`Tags: ${names}`}
      >
        {shown.map((tag) => (
          <span
            key={tag.tagId}
            aria-hidden="true"
            className="h-full min-w-1 flex-1"
            style={{ backgroundColor: tag.color ?? '#64748b' }}
          />
        ))}
      </span>
    );
  }
  return (
    <span className={cn('flex shrink-0 items-center gap-1', className)}>
      {shown.map((tag) => {
        const color = tag.color ?? '#64748b';
        return (
          <span
            key={tag.tagId}
            title={tag.name}
            className="inline-flex max-w-[92px] items-center gap-1 rounded-full border px-1.5 py-[1px] text-[9px] font-medium leading-none text-foreground"
            style={{ backgroundColor: `${color}1f`, borderColor: color }}
          >
            <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
            <span className="truncate">{tag.name}</span>
          </span>
        );
      })}
      {overflow > 0 ? (
        <span className="shrink-0 rounded-full border border-border px-1 py-[1px] text-[9px] font-medium leading-none text-muted-foreground">
          +{overflow}
        </span>
      ) : null}
    </span>
  );
}
